/**
 * Inlined literals for the two `@vanguard/vg-otel-js/common` enum members this app configures.
 *
 * Why this file exists: `src/environments/index.ts` re-exports `environment.prod.external`, and
 * Angular only swaps that file via `fileReplacements` at *build* time — so every spec that touches
 * `@environment` loads the production environment. Importing these enums as *values* therefore
 * drags the whole OpenTelemetry SDK (`@opentelemetry/api`, `core`, `resources`, `sdk-trace-base`,
 * `instrumentation`, `api-logs` and `@vanguard/vg-otel-js`) into any suite that reaches
 * `@environment`, which is most of them.
 *
 * `import type` is erased at emit, so this module requires nothing at runtime. Production is
 * unaffected: the real enums are still what the built app configures, because these literals are
 * the same strings.
 *
 * The literals cannot silently drift. Each raw literal is declared *without* a cast and then
 * asserted against the real enum member, so changing a literal here — or the library changing its
 * value — fails `tsc`. Asserting before the cast matters: a cast would satisfy the assertion
 * trivially and the guard would be worthless.
 */
import type { CollectorEnvironment, CollectorLocation } from '@vanguard/vg-otel-js/common';

// Raw, uncast literals — these are what the assertions below actually check.
const PUBLIC = 'PUBLIC';
const PRODUCTION = 'PRODUCTION';

type Assert<T extends true> = T;

/** Compile-time proof that the inlined literals still match the library enums. */
export type OtelCollectorLiteralsInSync = [
  Assert<`${CollectorLocation.PUBLIC}` extends typeof PUBLIC ? true : false>,
  Assert<`${CollectorEnvironment.PRODUCTION}` extends typeof PRODUCTION ? true : false>,
];

// Cast to the specific enum *member*, so anything expecting that member still type-checks.
export const COLLECTOR_LOCATION_PUBLIC = PUBLIC as CollectorLocation.PUBLIC;
export const COLLECTOR_ENVIRONMENT_PRODUCTION = PRODUCTION as CollectorEnvironment.PRODUCTION;
